import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { getApplicationData } from 'selectors/getApplicationData';
import { getLoanOfferPayload } from 'selectors/getLoanOfferData';
import { getApplicationData as getApplicationDataThunk, getLoanOffer, updateApplicationCurrentFlow } from 'thunks';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { CurrentFlow } from 'enums/CurrentFlow';
import { RoutePath } from 'enums/Routes';
import { useNavigate } from 'hooks/useNavigate';
import { useQueryParams } from 'hooks/useQueryParam';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { getUtmTagsVariables } from 'utils/getUtmTags';
import { OFFER_AVAILABLE_FROM_DASHBOARD_STATE } from 'utils/navigationStates';
import { FLOW_TO_STEP_MAP } from 'handlers/applicationStep';

const usePursueLoanOffer = (): { pursueLoan: () => Promise<void>; isPursuingLoan: boolean } => {
  const { application } = useSelector(getApplicationData);
  const loanOfferPayload = useSelector(getLoanOfferPayload);
  const [isPursuingLoan, setIsPursuingLoan] = useState(false);
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigate = useNavigate();
  const params = useQueryParams();

  const pursueLoan = useCallback(async () => {
    if (!application) return;

    const originatingFlow = application.currentFlow;
    const returnFlowAfterRejection =
      originatingFlow === CurrentFlow.FinancialCheckup ? CurrentFlow.FinancialCheckup : CurrentFlow.AICoach;

    setIsPursuingLoan(true);
    try {
      await dispatchWithUnwrap(
        updateApplicationCurrentFlow({
          applicationId: application.id,
          currentFlow: CurrentFlow.DebtConsolidation,
        }),
      );
      const response = await dispatchWithUnwrap(
        getLoanOffer({
          ...loanOfferPayload,
          utm: getUtmTagsVariables(params),
          currentFlow: CurrentFlow.DebtConsolidation,
        }),
      );
      const applicationId = response.data.application_id;
      const loaded = applicationId ? await dispatchWithUnwrap(getApplicationDataThunk(applicationId)) : undefined;
      const status = response.data.application_status;

      if (status === ApplicationStatusName.Rejected && applicationId) {
        await dispatchWithUnwrap(
          updateApplicationCurrentFlow({
            applicationId,
            currentFlow: returnFlowAfterRejection,
          }),
        );
        navigate(RoutePath.OfferStatus);
        return;
      }

      if (status === ApplicationStatusName.OfferAvailable) {
        navigate(RoutePath.OfferAvailable, OFFER_AVAILABLE_FROM_DASHBOARD_STATE);
        return;
      }

      navigate(FLOW_TO_STEP_MAP[CurrentFlow.DebtConsolidation](loaded?.application).path);
    } catch {
      if (originatingFlow && originatingFlow !== CurrentFlow.DebtConsolidation) {
        await dispatchWithUnwrap(
          updateApplicationCurrentFlow({
            applicationId: application.id,
            currentFlow: originatingFlow,
          }),
        ).catch(() => undefined);
      }
    } finally {
      setIsPursuingLoan(false);
    }
  }, [application, dispatchWithUnwrap, loanOfferPayload, navigate, params]);

  return { pursueLoan, isPursuingLoan };
};

export default usePursueLoanOffer;
